/**
 * Which routes only the desktop server serves, and why (MA-414). The dashboard
 * is one bundle for every surface; this list is the one place that says what
 * the cloud doesn't have. A route is here for one of two reasons:
 *
 *   - `local` — it can only work on the operator's machine (the Claude CLI,
 *     Ollama, how this desktop is reached, the installed app, its logs);
 *   - `gap` — the cloud should have it and doesn't yet (§4.7 Principle 0); the
 *     finding that tracks it is named.
 *
 * `tests/surface-routes.test.ts` keeps the list true: every route the
 * dashboard calls that the cloud doesn't serve is here, and nothing here is
 * served by the cloud. On the cloud the dashboard's `api()` answers a listed
 * route with its reason instead of sending a request that would 404.
 *
 * Patterns are paths with `:param` segments; one ending in `/*` covers that
 * path and everything below it.
 */

export interface DesktopOnlyRoute {
  kind: 'local' | 'gap';
  /** What the operator is told on the cloud. */
  reason: string;
  /** The finding that tracks a gap. */
  finding?: string;
}

const local = (reason: string): DesktopOnlyRoute => ({ kind: 'local', reason });
const gap = (finding: string, reason: string): DesktopOnlyRoute => ({ kind: 'gap', reason, finding });

export const DESKTOP_ONLY_ROUTES: Readonly<Record<string, DesktopOnlyRoute>> = {
  // ── Local by nature ───────────────────────────────────────────────────────
  '/api/claude/*': local('The Claude subscription runs through the Claude Code CLI on the desktop.'),
  '/api/ollama/*': local('Ollama is a program on the desktop machine.'),
  '/api/network/*': local('Phone access (relay and pairing tokens) is set on the desktop.'),
  '/api/system/*': local('Restarting and updating apply to the installed desktop app.'),
  '/api/updates/check': local('Update checks are for the installed desktop app.'),
  '/api/health': local("The desktop server's restart probe (the cloud reports at /health)."),
  '/api/cloud/*': local('Linking to the cloud account happens from the desktop.'),
  '/api/credentials/:id/reveal': local('The cloud never sends a stored secret back out.'),
  '/api/external-mcp/*': local('External MCP servers run as programs on the desktop.'),
  '/api/diagnostics/*': local("Diagnostics are the desktop app's own logs and reports."),

  // ── Gaps — the cloud should have these ─────────────────────────────────────
  '/api/email/*': gap('MA-391', "The cloud's email tools work; the Email page's routes aren't in the cloud yet."),
  '/api/analytics/*': gap('MA-330', "Analytics aren't in the cloud yet."),
  '/api/alerts': gap('MA-331', "Alerts aren't in the cloud yet."),
  '/api/skills/*': gap('MA-332', "Skills aren't in the cloud yet."),
  '/api/chat/answer': gap('MA-355', "Approval questions can't be answered in the cloud yet."),
  '/api/chat/question': gap('MA-355', "Approval questions can't be answered in the cloud yet."),
  '/api/chat/stop': gap('MA-391', "Stopping a running turn isn't in the cloud yet."),
  '/api/chat/sessions/:id/summarize/*': gap('MA-391', "Summarizing a conversation isn't in the cloud yet."),
  '/api/chat/sessions/:id/turn/stream': gap('MA-391', "Re-attaching to a running turn isn't in the cloud yet."),
  '/api/chat/upload': gap('MA-391', "File uploads aren't in the cloud yet."),
  '/api/chat/sessions/:id/attachments/:attachmentId': gap('MA-391', "File uploads aren't in the cloud yet."),
  '/api/media': gap('MA-391', "File uploads aren't in the cloud yet."),
  '/api/operator/avatar': gap('MA-391', "Profile pictures aren't in the cloud yet."),
  '/api/transcribe/*': gap('MA-391', "Speech-to-text isn't in the cloud yet."),
  '/api/files/*': gap('MA-347', 'Project files need the cloud sandbox.'),
  '/api/github/runners': gap('MA-343', "GitHub tools aren't in the cloud yet."),
};

/**
 * Does `pattern` cover `path` (query string ignored)? A `:param` segment on
 * either side matches any segment, so declared route paths (`/api/x/:id`)
 * can be checked against the list too.
 */
export function patternCovers(pattern: string, path: string): boolean {
  const want = pattern.split('/');
  const have = (path.split('?')[0] ?? '').replace(/\/+$/, '').split('/');
  const prefix = want[want.length - 1] === '*';
  const fixed = prefix ? want.slice(0, -1) : want;
  if (prefix ? have.length < fixed.length : have.length !== fixed.length) return false;
  return fixed.every((seg, i) => seg === have[i] || seg.startsWith(':') || Boolean(have[i]?.startsWith(':')));
}

/** The entry covering `path` when only the desktop serves it; undefined when every surface does. */
export function desktopOnlyRoute(path: string): DesktopOnlyRoute | undefined {
  for (const [pattern, route] of Object.entries(DESKTOP_ONLY_ROUTES)) {
    if (patternCovers(pattern, path)) return route;
  }
  return undefined;
}
